import { zodResolver } from "@hookform/resolvers/zod";
import { ReplyIcon, SendIcon, XIcon } from "lucide-react";
import { ChangeEvent, KeyboardEvent, useEffect, useRef } from "react";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { z } from "zod";

import { Column, Row, Text } from "@/components";
import { CommunicationsScopeBanner } from "@/components/user-sheet/comms/CommunicationsScopeBanner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { email as emailUtils } from "@/lib/email";
import { Email } from "@/types";

const sendEmailSchema = z.object({
  subject: z.string().trim().min(1, "Required"),
  body: z.string().trim().min(1, "Required"),
});

export type SendEmailComposerValues = z.infer<typeof sendEmailSchema>;

export const EmailComposer = ({
  autoFocus = false,
  candidateName,
  disabled,
  emailDraft,
  onClearReplyTarget,
  onEmailDraftChange,
  onSend,
  replyTarget,
}: {
  autoFocus?: boolean;
  candidateName: string;
  disabled: boolean;
  emailDraft: SendEmailComposerValues;
  onClearReplyTarget: () => void;
  onEmailDraftChange: (values: SendEmailComposerValues) => void;
  onSend: (values: SendEmailComposerValues) => void;
  replyTarget: Email | null;
}) => {
  /* React Hooks */
  const form = useForm<SendEmailComposerValues>({
    resolver: zodResolver(sendEmailSchema),
    // Seed from the lifted draft so the text survives tab switches.
    defaultValues: emailDraft,
  });
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const prevReplyTargetIdRef = useRef(replyTarget?.id);

  // Re-seed subject when reply target changes. Body always resets so the
  // composer feels fresh as the user moves between threads. Skipped when the
  // target hasn't changed (e.g. remount after a tab switch) so the lifted
  // draft isn't wiped. When a reply is started, drop the cursor straight into
  // the body so the user can just type.
  useEffect(() => {
    if (replyTarget?.id === prevReplyTargetIdRef.current) return;
    prevReplyTargetIdRef.current = replyTarget?.id;
    const subject = replyTarget
      ? emailUtils.formatReplySubject(replyTarget.subject)
      : "";
    form.reset({ subject, body: "" });
    onEmailDraftChange({ subject, body: "" });
    if (replyTarget) bodyRef.current?.focus();
  }, [replyTarget?.id, form, replyTarget, onEmailDraftChange]);

  useEffect(() => {
    if (!autoFocus || replyTarget) return;

    form.setFocus("subject");
  }, [autoFocus, form, replyTarget]);

  /* Handlers */
  const handleSubmit = (values: SendEmailComposerValues) => {
    onSend(values);
    const nextSubject = replyTarget
      ? emailUtils.formatReplySubject(replyTarget.subject)
      : "";
    form.reset({ subject: nextSubject, body: "" });
    onEmailDraftChange({ subject: nextSubject, body: "" });
  };

  // Cmd/Ctrl + Enter sends.
  const handleBodyKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      form.handleSubmit(handleSubmit)();
    }
  };

  /* Render */
  const render = () => (
    <Column className="gap-2 pb-0 pt-3">
      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col gap-3"
        >
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <EmailComposerSubjectLine
                disabled={disabled}
                field={{
                  ...field,
                  onChange: (e: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e);
                    onEmailDraftChange({
                      subject: e.target.value,
                      body: form.getValues("body"),
                    });
                  },
                }}
                onClearReplyTarget={onClearReplyTarget}
                replyTarget={replyTarget}
              />
            )}
          />
          <FormField
            control={form.control}
            name="body"
            render={({ field }) => (
              <FormItem className="space-y-0">
                <FormControl>
                  <Textarea
                    {...field}
                    ref={(el) => {
                      field.ref(el);
                      bodyRef.current = el;
                    }}
                    className="resize-none border-slate-300/70 bg-primary-50 focus-visible:ring-primary-300"
                    disabled={disabled}
                    onChange={(e) => {
                      field.onChange(e);
                      onEmailDraftChange({
                        subject: form.getValues("subject"),
                        body: e.target.value,
                      });
                    }}
                    onKeyDown={handleBodyKeyDown}
                    placeholder="Write a message…"
                    rows={4}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Row className="items-center gap-2">
            <CommunicationsScopeBanner
              candidateName={candidateName}
              className="min-w-0 flex-1"
              communicationsTab="external"
            />
            <Button
              className="h-9 shrink-0 rounded-full"
              disabled={disabled}
              size="sm"
              type="submit"
            >
              <SendIcon className="h-4 w-4" />
              Send
            </Button>
          </Row>
        </form>
      </Form>
    </Column>
  );

  return render();
};

// Subject row: an editable input for a new email, or a locked "Replying in"
// context line (with a clear-to-new-email button) when replying into a thread.
const EmailComposerSubjectLine = ({
  disabled,
  field,
  onClearReplyTarget,
  replyTarget,
}: {
  disabled: boolean;
  field: ControllerRenderProps<SendEmailComposerValues, "subject">;
  onClearReplyTarget: () => void;
  replyTarget: Email | null;
}) => {
  /* Render */
  const render = () => {
    if (!replyTarget) {
      return (
        <FormItem className="space-y-0">
          <FormControl>
            <Input
              {...field}
              aria-label="Email subject"
              className="border-slate-300/70 bg-primary-50 focus-visible:ring-primary-300"
              disabled={disabled}
              placeholder="Subject"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );
    }

    return (
      <Row className="items-center gap-1.5">
        <ReplyIcon className="h-3 w-3 shrink-0 text-slate-400" />
        <Text className="shrink-0 text-[11px] font-normal text-slate-500">
          Replying in
        </Text>
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-700" />
        <Text className="truncate text-[11px] font-semibold text-slate-900">
          {field.value}
        </Text>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Exit thread"
                className="ml-auto h-6 w-6 shrink-0 p-0 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                onClick={onClearReplyTarget}
                size="sm"
                type="button"
                variant="ghost"
              >
                <XIcon className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Exit thread</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Row>
    );
  };

  return render();
};
